import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, Input, ViewChild } from '@angular/core';
import { DataService } from 'src/core/services/data.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { Table, TableModule } from 'primeng/table';
import { ChartConfiguration, ChartType, Chart, ChartDataset } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { ChatService } from 'src/core/services/chat.service';
import { DaterangeService } from 'src/core/services/daterange.service';
import { debounceTime, defer, distinctUntilChanged, iif, map, merge, Subject, Subscription, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { ModalService } from 'src/core/services/modal.service';
import { LoadingService } from 'src/core/services/loading.service';
import { WidgetService } from 'src/core/services/widget.service';
import { selectedWidget, workareaSize } from 'src/core/types/workarea.types';
import { NgShuiModule } from '@shui/angular';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { AutocheckData } from 'src/core/types/systemhealth.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiResponse } from 'src/core/types/general.types';
import { SortEvent } from 'primeng/api';
import { PowerbiService } from 'src/core/services/powerbi.service';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { environment } from 'src/environments/environment';
import { DeviceService } from 'src/core/services/device.service';
import { shouldMarkPromptAsOptionAnswer } from 'src/core/utils/chat-option-answer.util';

// TableModule, ButtonModule, BaseChartDirective, FormsModule, DropdownModule, InputTextModule, SelectModule, TagModule, CheckboxModule, NgShuiModule

@Component({
  selector: 'app-autocheck-details',
  imports: [CommonModule, TableModule, BaseChartDirective, NgShuiModule, CommonModule, FormsModule, TranslocoModule],
  templateUrl: './autocheck-details.component.html',
  styleUrl: './autocheck-details.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AutocheckDetailsComponent {
  private readonly autocheckChartColor = '#FBBF28';
  private readonly autocheckChartFillColor = 'rgba(251, 191, 40, 0.15)';
  private readonly maglineChartColor = '#808080';
  private readonly maglineChartFillColor = 'rgba(128, 128, 128, 0.18)';

  constructor(private dataService: DataService, private chatService: ChatService, private selectedSystemService: SelectedsystemService, private daterangeService: DaterangeService, private modalService: ModalService, private loadingService: LoadingService, private widgetService: WidgetService, private powerBiService: PowerbiService, private selectedContentService: SelectedContentService, private deviceService: DeviceService, private translocoService: TranslocoService) { }

  @ViewChild('table') table: Table;
  @ViewChild('tooltip') tooltip: any;

  autocheckData: any = null;
  isLoading: boolean = true;
  isError: boolean = false;
  expandedRows: any = {};

  searchTerm: string = '';
  private search$ = new Subject<{ term: string; cb: (val: string) => void }>();
  private sub!: Subscription;
  selectedRange: number;

  @Input() tabs: any[] = [];
  selectedWidget: string = "None";
  displayedTabs: any[] = [];
  activeTab: string = "Activities";
  workareaSize: workareaSize = "small";
  screenSize: string = 'Large';
  onMaximize = this.widgetService.onMaximize.bind(this.widgetService);
  onMinimize = this.widgetService.onMinimize.bind(this.widgetService);
   onExpand = (event: any) => {
    if(this.workareaSize !== "fullscreen") {
      this.widgetService.onExpand();
    } else {
      this.widgetService.$workareaSize.next("large");
    }
  }
  switchTab = this.widgetService.switchTab.bind(this.widgetService);
  businessPartnerMode = environment.businessPartnerMode;
  changeRange = (range: number, type: 'systemHealth' | 'serviceHistory') => {
    this.daterangeService.changeRange(range, type);
  };

  public chartConfigs: { [key: string]: ChartConfiguration<'line'> } = {};

  public chartConfigDetails: { [key: string]: ChartConfiguration<'line'> } = {};

  private baseDatasetStyle: Partial<ChartDataset<'line'>> = {
    pointBorderWidth: 0,
    pointRadius: 2.5,
    pointHoverRadius: 2.5,
    borderWidth: 4,
    tension: 0.35,
    fill: 'origin'
  };

  private destroyRef = inject(DestroyRef);


  ngOnInit(): void {

    this.sub = this.search$.pipe(
      map(({ term, cb }) => ({ term: term?.trim() ?? '', cb })),
      debounceTime(300),
      distinctUntilChanged((a, b) => a.term === b.term)
    ).subscribe(({ term, cb }) => cb(term));

    this.loadingService.$isAutocheckLoading.subscribe((isAutocheckLoading) => {
      this.isLoading = isAutocheckLoading;
    });

    this.loadingService.$isAutocheckError.subscribe((error) => {
      this.isError = error;
    });

    //Check which tabs to display
    this.widgetService.$selectedWidget.subscribe({
      next: (widget: selectedWidget) => {
        this.selectedWidget = widget;
        this.displayedTabs = this.tabs.filter(tab => tab.selectedWidget === widget);

      }
    });

    //Check which tab is currently active
    this.widgetService.$activeTab.subscribe((tab) => {
      if (tab) {
        this.activeTab = tab;
      }
      else {
        this.activeTab = this.displayedTabs[0]?.name;
      }
    });

    //Need to know the current workarea size as well to display correct icons
    this.widgetService.$workareaSize.subscribe({
      next: (size: workareaSize) => {
        this.workareaSize = size;
      }
    });

    this.deviceService.size$.subscribe((size) => {
      this.screenSize = size;
    });

    //Listening to changed Daterange
    const range$ = this.daterangeService.$selectedRangeSystemHealth;

    // Trigger when range changes (normal), or when user clicks refresh (force fetch)
    const triggers$ = merge(
      range$.pipe(map(rangeValue => ({ rangeValue, force: false }))),
      this.selectedSystemService.$refreshAutochecks.pipe(
        withLatestFrom(range$),
        map(([, rangeValue]) => ({ rangeValue, force: true }))
      )
    );

    triggers$.pipe(
      tap(() => {
        // ✅ Reset before API call
        this.loadingService.$isAutocheckLoading.next(true);
        this.loadingService.$isAutocheckError.next(false);
        this.autocheckData = null;
        this.chartConfigs = {};
        this.chartConfigDetails = {};
      }),
      switchMap(({ rangeValue, force }) => defer(() => {
        const now = new Date();
        const lastPulled = this.selectedSystemService.autocheckPullDate;
        const pulledFor = this.selectedSystemService.autocheckPulledFor;

        const serial = this.selectedContentService.currentSystem?.SerialNumber || '';
        const material = this.selectedContentService.currentSystem?.MaterialNumber || '';

        const shouldFetch =
          force ||
          !lastPulled ||
          (now.getTime() - new Date(lastPulled).getTime()) > 5 * 60 * 1000 ||
          pulledFor !== (serial + material);

        return iif(
          () => shouldFetch,
          this.dataService.getAutocheck(material, serial).pipe(
            tap(({ response, status }: ApiResponse<AutocheckData>) => {
              console.log(force ? 'Forced refresh of autocheck data' : 'Fetching new autocheck data');
              this.selectedSystemService.autocheckPullDate = now;
              this.selectedSystemService.autocheckPulledFor = serial + material;

              if (status === 200) {
                this.selectedSystemService.$autocheck.next(response);
              }
              else if (status === 204) {
                this.selectedSystemService.$autocheck.next(null);
                this.loadingService.$isAutocheckEmpty.next(true);
              }
            })
          ),
          this.selectedSystemService.$autocheck.pipe(
            take(1),
            tap(() => console.log('Using cached autocheck data')),
            map((cached): ApiResponse<AutocheckData> => ({
              response: cached,
              status: 200 // or some fixed value to simulate a successful cached response
            }))
          )
        ).pipe(
          map(({ response, status }: ApiResponse<AutocheckData>) => ({ response, rangeValue }))
        );
      })),
      // 👇 Auto-unsubscribe when the component is destroyed
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: ({ response, rangeValue }) => {

        //Assign data
        this.selectedRange = rangeValue;

        const customData = response?.L1_data?.map(r => (
          {
            ...r,
            name: r['name'] !== 'Others' ? r['name'] : 'Others (Click on Charts for Details)',
            details: r['name'] !== 'Others' ? r['details'] : [],
            custom: r['name'] !== 'Others' ? r['name'] + ' - ' + r['details'].map(c => c.name).join(', ') : 'Others'
          })
        ) || [];

        this.autocheckData = customData;
        //console.log("Steffen", response);
        this.powerBiService.setAutocheckData(customData);

        //Generate Charts from Data
        customData.forEach(row => {
          const key = row.name;
          const chartColor = this.getAutocheckChartColor(key);
          this.chartConfigs[key] = this.createChart(row['failures']['failed']['trend'][rangeValue + ''], chartColor);

          row.details.forEach(detail => {
            const detailKey = detail.name;
            this.chartConfigDetails[detailKey] = this.createChart(detail['failures']['failed']['trend'][rangeValue + ''], chartColor);
          });
        });
        this.loadingService.$isAutocheckLoading.next(false);
      },
      error: (error) => {
        console.error("Error fetching autocheck data:", error);
        this.autocheckData = [];
        this.loadingService.$isAutocheckError.next(true);
        this.loadingService.$isAutocheckLoading.next(false);
      }
    });

  }

  onSearchChange(term: string, filterCb: (val: string) => void) {
    this.search$.next({ term, cb: filterCb });
  }


  onTableFilter(event) {

    console.log('Table filter event:', event);

    const customFilter = event.filters['custom'];

    const hasSearch = Array.isArray(customFilter)
      ? customFilter.some(f => !!f.value)
      : !!customFilter?.value;

    if (hasSearch) {
      this.expandedRows = {};
      for (const row of event.filteredValue ?? []) {
        this.expandedRows[row.name] = true;
      }
    } else {
      this.expandedRows = {}; // Collapse if not filtering by text
    }
  }

  onSearchApply(event: any) {
    console.log('Search term applied:', event);
    this.searchTerm = event.value?.toLowerCase() || '';
  }

  highlight(text: string): string {
    if (!this.searchTerm || !text) return text;

    const escaped = this.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    return text.replace(regex, '<mark class="hl">$1</mark>');
  }

  askChat(testname: string) {
    this.widgetService.$chatPipSize.next('maximized');
    if (this.chatService.isStreaminginProgress()) return;
    const prompt = this.translocoService.translate('autocheckDetails.prompt.fixAutocheck', {
      testName: testname,
      serialNumber: this.selectedContentService.currentSystem?.SerialNumber || ''
    });

    if (shouldMarkPromptAsOptionAnswer(this.chatService.messages?.value)) {
      this.chatService.addMessage(prompt, true, undefined, true);
      return;
    }

    this.chatService.addMessage(prompt, true);

  }

  openCharts(testgroup?: string) {
    this.modalService.$modalChanged.next({ modal: "autocheck", open: true, filter: [{ value: testgroup, type: "text" }] })
  }

  hasNonZeroTrend(rowData?: number[] | null): boolean {
    return Array.isArray(rowData) && rowData.some(value => Number(value) !== 0);
  }

  private getAutocheckChartColor(name: string) {
    return name === 'Magline'
      ? { border: this.maglineChartColor, fill: this.maglineChartFillColor }
      : { border: this.autocheckChartColor, fill: this.autocheckChartFillColor };
  }

  createChart(rowData: number[], chartColor: { border: string; fill: string }): ChartConfiguration<'line'> {
    return {

      type: 'line',
      data: {
        labels: Array(rowData.length).fill(''),
        datasets: [{
          ...this.baseDatasetStyle,
          data: rowData,
          backgroundColor: chartColor.fill,
          borderColor: chartColor.border,
          pointBackgroundColor: chartColor.border,
          pointBorderColor: chartColor.border
        }]
      },
      options: {
        devicePixelRatio: 4,
        interaction: {
          mode: null
        },
        responsive: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: { display: false },
          y: { display: false }
        }
      }
    };
  }

  refresh() {
    this.selectedSystemService.$refreshAutochecks.next();
  }

  customSort(event: SortEvent) {
    const field = event.field;
    const order = event.order;

    event.data.sort((a, b) => {
      let val1: any;
      let val2: any;

      switch (field) {
        case 'failed':
          val1 = a['failures']['failed'][this.selectedRange + ''] ?? 0;
          val2 = b['failures']['failed'][this.selectedRange + ''] ?? 0;
          break;
        case 'passed':
          val1 = a['passed'][this.selectedRange + ''] ?? 0;
          val2 = b['passed'][this.selectedRange + ''] ?? 0;
          break;
        case 'last_failed_date': // assuming this is a date field
          val1 = new Date(a.date_last_failure);
          val2 = new Date(b.date_last_failure);
          break;

        case 'name': // string field
          val1 = a.name?.toLowerCase() ?? '';
          val2 = b.name?.toLowerCase() ?? '';
          break;

        default:
          val1 = a[field];
          val2 = b[field];
          break;
      }

      let result = 0;
      if (val1 < val2) result = -1;
      else if (val1 > val2) result = 1;

      return result * order;
    });
  }

  showTooltip(key: string, event) {
    this.tooltip.nativeElement.target = event?.target;
    this.tooltip.nativeElement.innerHTML = this.translocoService.translate(key);
    this.tooltip.nativeElement.visible = true;
  }
  
  goToATA(url) {
    if(url) {
      window.open("https://"+ url, '_blank');
    } else {
      console.error('ATA information is not available.');
    }
  }
  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

}
